import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Popover,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Sparkles, SpellCheck, WandSparkles } from "lucide-react";

import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  icon: {
    color: ({ open }) =>
      open ? theme.palette.primary.main : theme.palette.text.secondary,
  },
  popoverPaper: {
    width: 420,
    maxWidth: "calc(100vw - 24px)",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.18)",
  },
  header: {
    padding: "14px 16px 8px",
  },
  title: {
    fontWeight: 600,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: 3,
  },
  option: {
    alignItems: "flex-start",
    padding: "10px 16px",
  },
  optionIcon: {
    minWidth: 36,
    color: theme.palette.primary.main,
    marginTop: 3,
  },
  content: {
    padding: "8px 16px 14px",
  },
  suggestion: {
    marginTop: 10,
    padding: 12,
    maxHeight: 220,
    overflowY: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.05)" : "#f7f9fc",
  },
  loading: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minHeight: 92,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    padding: "0 16px 14px",
  },
  statusText: {
    padding: "12px 16px 16px",
  },
}));

const MODE_LABELS = {
  correction: "Corrigir português",
  improvement: "Melhorar o texto",
};

/**
 * Oferece uma sugestão da Pink para o rascunho atual sem alterar ou enviar a mensagem automaticamente.
 */
const MessageSuggestionButton = ({ ticketId, text, disabled, onApply }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [view, setView] = useState("options");
  const [mode, setMode] = useState(null);
  const [suggestion, setSuggestion] = useState("");
  const [originalText, setOriginalText] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const requestIdRef = useRef(0);
  const open = Boolean(anchorEl);
  const classes = useStyles({ open });
  const isDisabled = disabled || !String(text || "").trim();

  const resetSuggestion = () => {
    setView("options");
    setMode(null);
    setSuggestion("");
    setOriginalText("");
    setErrorMessage("");
  };

  const handleClose = () => {
    requestIdRef.current += 1;
    setAnchorEl(null);
    resetSuggestion();
  };

  useEffect(() => {
    handleClose();
    // O ticket delimita o rascunho e invalida qualquer sugestão pendente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  useEffect(
    () => () => {
      requestIdRef.current += 1;
    },
    []
  );

  useEffect(() => {
    if (open && originalText && text !== originalText) {
      requestIdRef.current += 1;
      resetSuggestion();
    }
  }, [open, originalText, text]);

  const handleOpen = (event) => {
    resetSuggestion();
    setAnchorEl(event.currentTarget);
  };

  const handleRequestSuggestion = async (selectedMode) => {
    const requestId = requestIdRef.current + 1;
    const draft = text;
    requestIdRef.current = requestId;
    setMode(selectedMode);
    setOriginalText(draft);
    setErrorMessage("");
    setView("loading");

    try {
      const { data } = await api.post(`/messages/${ticketId}/text-suggestion`, {
        text: draft,
        mode: selectedMode,
      });

      if (requestIdRef.current !== requestId) return;

      setSuggestion(data.suggestion);
      setView(data.changed ? "preview" : "unchanged");
    } catch (error) {
      if (requestIdRef.current !== requestId) return;

      setErrorMessage(
        error?.response?.data?.error ||
          "Não foi possível gerar a sugestão agora. Seu texto foi preservado."
      );
      setView("error");
    }
  };

  const handleApply = () => {
    const selectedSuggestion = suggestion;
    handleClose();
    onApply(selectedSuggestion);
  };

  return (
    <>
      <Tooltip title="Sugestão da Pink">
        <span>
          <IconButton
            aria-label="Sugestão da Pink"
            aria-haspopup="true"
            aria-expanded={open}
            onClick={handleOpen}
            disabled={isDisabled}
          >
            <Sparkles
              aria-hidden="true"
              className={classes.icon}
              size={21}
              strokeWidth={1.8}
            />
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        PaperProps={{ className: classes.popoverPaper }}
      >
        {view === "options" && (
          <>
            <div className={classes.header}>
              <Typography className={classes.title}>
                Sugestão da Pink
              </Typography>
              <Typography variant="body2" className={classes.subtitle}>
                Escolha como deseja revisar seu texto.
              </Typography>
            </div>
            <List disablePadding>
              <ListItem
                button
                className={classes.option}
                onClick={() => handleRequestSuggestion("correction")}
              >
                <ListItemIcon className={classes.optionIcon}>
                  <SpellCheck aria-hidden="true" size={19} strokeWidth={1.8} />
                </ListItemIcon>
                <ListItemText
                  primary="Corrigir português"
                  secondary="Ortografia, pontuação e concordância, sem mudar seu tom."
                />
              </ListItem>
              <ListItem
                button
                className={classes.option}
                onClick={() => handleRequestSuggestion("improvement")}
              >
                <ListItemIcon className={classes.optionIcon}>
                  <WandSparkles
                    aria-hidden="true"
                    size={19}
                    strokeWidth={1.8}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Melhorar o texto"
                  secondary="Reescreve com mais clareza, impacto e tom profissional."
                />
              </ListItem>
            </List>
          </>
        )}

        {view === "loading" && (
          <div className={`${classes.content} ${classes.loading}`}>
            <CircularProgress size={24} />
            <Typography variant="body2">
              A Pink está preparando a sugestão...
            </Typography>
          </div>
        )}

        {view === "preview" && (
          <>
            <div className={classes.content}>
              <Typography className={classes.title}>
                Sugestão da Pink
              </Typography>
              <Typography variant="caption" className={classes.subtitle}>
                {MODE_LABELS[mode]}
              </Typography>
              <Typography variant="body2" className={classes.suggestion}>
                {suggestion}
              </Typography>
            </div>
            <div className={classes.actions}>
              <Button onClick={handleClose}>Descartar</Button>
              <Button color="primary" variant="contained" onClick={handleApply}>
                Usar sugestão
              </Button>
            </div>
          </>
        )}

        {view === "unchanged" && (
          <>
            <Typography variant="body2" className={classes.statusText}>
              Seu texto já está adequado para este tipo de revisão.
            </Typography>
            <div className={classes.actions}>
              <Button color="primary" onClick={() => resetSuggestion()}>
                Voltar
              </Button>
            </div>
          </>
        )}

        {view === "error" && (
          <>
            <Typography variant="body2" className={classes.statusText}>
              {errorMessage}
            </Typography>
            <div className={classes.actions}>
              <Button onClick={handleClose}>Descartar</Button>
              <Button color="primary" onClick={() => resetSuggestion()}>
                Tentar novamente
              </Button>
            </div>
          </>
        )}
      </Popover>
    </>
  );
};

export default MessageSuggestionButton;
